import { useEffect, useMemo } from 'react'
import { Plane, Route, X } from 'lucide-react'
import type { LiveAircraft, WatchRuleInput } from '@/types/airTraffic'
import type { SelectionOverlay } from '../AirMap'
import { AIR, CLASS_COLOR, FONT_MONO, classify, fmtAlt } from '../theme'
import type { BBox } from '../useAirLive'
import { ActionButton, Chip, Empty, SectionLabel, downloadReport } from '../ui'
import useRoutes, { routeLabel, routeLines } from '../useRoutes'

interface Props {
  bbox: BBox | null
  aircraft: LiveAircraft[]
  watched: Set<string>
  areaOn: boolean
  onClose: () => void
  onSelection: (s: SelectionOverlay) => void
  onOpenAircraft: (hex: string, lon?: number, lat?: number) => void
  onOpenOperator: (name: string) => void
  onWatch: (rule: WatchRuleInput) => Promise<void>
}

function top(counts: Record<string, number>, n = 6) {
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, n)
}

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number) {
  const r = Math.PI / 180
  const a = Math.sin(((lat2 - lat1) * r) / 2) ** 2 + Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(((lon2 - lon1) * r) / 2) ** 2
  return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** "This view": what is flying in the current map window, notable first. */
export default function AreaInspector({ bbox, aircraft, watched, areaOn, onClose, onSelection, onOpenAircraft, onOpenOperator, onWatch }: Props) {
  const { routes, busy: routesBusy, load: loadRoutes } = useRoutes()
  useEffect(() => {
    onSelection({ routes: routeLines(routes) })
  }, [routes, onSelection])

  const stats = useMemo(() => {
    const types: Record<string, number> = {}
    const ops: Record<string, number> = {}
    let mil = 0
    let priv = 0
    let emg = 0
    for (const a of aircraft) {
      if (a.type) types[a.type] = (types[a.type] || 0) + 1
      if (a.op) ops[a.op] = (ops[a.op] || 0) + 1
      if (a.mil) mil++
      if (a.ladd || a.pia) priv++
      if (a.emg) emg++
    }
    const notable = aircraft
      .filter((a) => a.emg || a.mil || a.ladd || a.pia || watched.has(a.hex))
      .sort((a, b) => Number(!!b.emg) - Number(!!a.emg) || Number(!!b.mil) - Number(!!a.mil))
    return { types: top(types), ops: top(ops), mil, priv, emg, notable }
  }, [aircraft, watched])

  const center = bbox ? { lon: (bbox[0] + bbox[2]) / 2, lat: (bbox[1] + bbox[3]) / 2 } : null
  // FR24 routes for up to 60 airborne aircraft: notable first, then nearest the view centre.
  const routeTargets = useMemo(() => {
    const c = center
    return aircraft
      .filter((a) => a.cs && !a.gnd && !(a.hex in routes))
      .sort((a, b) => {
        const na = a.emg || a.mil || a.ladd || a.pia ? 0 : 1
        const nb = b.emg || b.mil || b.ladd || b.pia ? 0 : 1
        if (na !== nb) return na - nb
        if (!c) return 0
        return Math.hypot(a.lat - c.lat, a.lon - c.lon) - Math.hypot(b.lat - c.lat, b.lon - c.lon)
      })
      .slice(0, 60)
      .map((a) => a.hex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraft, routes, bbox])
  const radiusKm = bbox && center ? Math.min(500, Math.max(10, Math.round(haversineKm(center.lat, center.lon, bbox[3], center.lon) * 0.8))) : 100

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-start justify-between gap-2 border-b px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: AIR.muted }}>
            This view
          </div>
          <div className="text-[16px] font-semibold" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
            {aircraft.length} aircraft
          </div>
          {bbox && (
            <div className="text-[11px]" style={{ color: AIR.muted, fontFamily: FONT_MONO }}>
              {bbox[1].toFixed(1)}, {bbox[0].toFixed(1)} → {bbox[3].toFixed(1)}, {bbox[2].toFixed(1)}
            </div>
          )}
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="rounded-sm p-1 hover:bg-white/5">
          <X className="h-4 w-4" style={{ color: AIR.muted }} />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto pb-4">
        {!areaOn && <Empty>Zoomed out past continent level, so only the worldwide military, emergency and privacy layers are counted. Zoom in for all traffic.</Empty>}
        <div className="flex flex-wrap gap-1 px-3 pt-3">
          <Chip color={AIR.military}>{stats.mil} military</Chip>
          <Chip color={AIR.privacy}>{stats.priv} privacy</Chip>
          <Chip color={AIR.emergency}>{stats.emg} emergency</Chip>
        </div>
        <div className="px-3 pt-2.5">
          <ActionButton
            onClick={() => loadRoutes(routeTargets)}
            busy={routesBusy}
            disabled={!routeTargets.length}
            title="Origin and destination from Flightradar24 for up to 60 airborne aircraft in view (notable first, then nearest the centre). Cached for 10 minutes."
          >
            <Route className="h-3.5 w-3.5" />
            {routeTargets.length ? `Show routes for ${routeTargets.length} · ~${routeTargets.length} FR24 credits` : 'Routes shown'}
          </ActionButton>
        </div>
        <SectionLabel>Top types</SectionLabel>
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 px-3 text-[12px]" style={{ fontFamily: FONT_MONO, color: AIR.text }}>
          {stats.types.map(([k, v]) => (
            <span key={k}>
              {k} <span style={{ color: AIR.muted }}>{v}</span>
            </span>
          ))}
        </div>
        <SectionLabel>Top operators</SectionLabel>
        {stats.ops.length === 0 && <Empty>No operator data for aircraft in view.</Empty>}
        {stats.ops.map(([k, v]) => (
          <button key={k} type="button" onClick={() => onOpenOperator(k)} className="flex w-full justify-between px-3 py-0.5 text-left text-[12px] hover:bg-white/[0.03]">
            <span className="truncate" style={{ color: AIR.text }}>
              {k}
            </span>
            <span style={{ color: AIR.muted, fontFamily: FONT_MONO }}>{v}</span>
          </button>
        ))}
        <SectionLabel>Notable · {stats.notable.length}</SectionLabel>
        {stats.notable.length === 0 && <Empty>No military, emergency, privacy-listed or watched aircraft in view.</Empty>}
        {stats.notable.slice(0, 80).map((a) => (
          <button
            key={a.hex}
            type="button"
            onClick={() => onOpenAircraft(a.hex, a.lon, a.lat)}
            className="flex w-full items-center gap-2 px-3 py-1 text-left text-[12px] hover:bg-white/[0.03]"
          >
            <Plane className="h-3 w-3 shrink-0" style={{ color: CLASS_COLOR[classify(a, watched)], transform: `rotate(${(a.trk ?? 45) - 45}deg)` }} />
            <span className="w-[76px] shrink-0 truncate" style={{ color: AIR.bright, fontFamily: FONT_MONO }}>
              {a.cs || a.reg || a.hex.toUpperCase()}
            </span>
            <span className="w-[74px] shrink-0 truncate" style={{ color: routes[a.hex] ? AIR.watched : AIR.muted, fontFamily: FONT_MONO }}>
              {routeLabel(routes[a.hex]) || a.type || '—'}
            </span>
            <span className="min-w-0 flex-1 truncate text-right" style={{ color: AIR.muted }}>
              {a.op || fmtAlt(a)}
            </span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5 border-t px-3 py-2.5" style={{ borderColor: AIR.line }}>
        <ActionButton tone="primary" disabled={!bbox} onClick={() => bbox && downloadReport('area', { bbox }, 'view')}>
          Report this area
        </ActionButton>
        <ActionButton
          disabled={!center}
          onClick={() => center && onWatch({ kind: 'area', value: `View ${center.lat.toFixed(1)}, ${center.lon.toFixed(1)}`, lat: center.lat, lon: center.lon, radius_km: radiusKm, match: 'notable' })}
          title={`Alert when a military, privacy-listed or emergency aircraft enters a ${radiusKm} km circle around the view centre`}
        >
          Watch area ({radiusKm} km)
        </ActionButton>
      </div>
    </div>
  )
}
